import { Plugin } from 'chart.js';
import { MatrixDataPoint } from 'chartjs-chart-matrix';
import { getColorScale } from '../../../../utils/color/color';
import { TelemetryColors } from '@shared/interfaces/telemetry';

function getTextColor(color: string): string {
  let r: number, g: number, b: number;
  if (color.startsWith('#')) {
    r = parseInt(color.slice(1, 3), 16);
    g = parseInt(color.slice(3, 5), 16);
    b = parseInt(color.slice(5, 7), 16);
  } else {
    [r, g, b] = color.match(/\d+/g)!.map(Number);
  }
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? '#111827' : '#ffffff';
}

export const createColorScaleLegendPlugin = (colorscale: [number, string][] | TelemetryColors): Plugin<'matrix'> => ({
  id: 'colorScaleLegend',
  afterDraw(chart) {
    const { ctx, chartArea } = chart;
    if (!ctx || !chartArea) return;

    const dataPoints = chart.data.datasets[0]?.data as MatrixDataPoint[] | undefined;
    if (dataPoints === undefined) return;

    const maxValue = Math.max(...dataPoints.map((p) => p.v || 0), 0);
    const { right, top, bottom } = chartArea;
    const legendX = right + 12;
    const legendWidth = 14;
    const legendHeight = bottom - top;

    const gradient = ctx.createLinearGradient(0, bottom, 0, top);
    gradient.addColorStop(0, getColorScale(colorscale, 0));
    gradient.addColorStop(1, getColorScale(colorscale, 1));

    ctx.save();
    ctx.fillStyle = gradient;
    ctx.fillRect(legendX, top, legendWidth, legendHeight);
    ctx.strokeStyle = '#d1d5db';
    ctx.lineWidth = 0.5;
    ctx.strokeRect(legendX, top, legendWidth, legendHeight);

    ctx.fillStyle = '#374151';
    ctx.font = '10px sans-serif';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillText(String(maxValue), legendX + legendWidth + 4, top);
    ctx.fillText('0', legendX + legendWidth + 4, bottom);
    ctx.restore();
  },
});

export const createMatrixLabelsPlugin = (colorscale: [number, string][] | TelemetryColors): Plugin<'matrix'> => ({
  id: 'matrixLabels',
  afterDatasetsDraw(chart) {
    const dataPoints = chart.data.datasets[0]?.data as MatrixDataPoint[] | undefined;
    if (!dataPoints?.length) return;
    const ctx = chart.ctx;
    const xScale = chart.scales['x'];
    const yScale = chart.scales['y'];
    if (!xScale || !yScale) return;
    const maxValue = Math.max(...dataPoints.map((p) => p.v || 0), 0);
    ctx.save();
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.font = '11px sans-serif';
    dataPoints.forEach((point) => {
      if (!point) return;
      const intensity = maxValue === 0 ? 0 : (point.v || 0) / maxValue;
      ctx.fillStyle = getTextColor(getColorScale(colorscale, intensity));
      ctx.fillText(String(point.v), xScale.getPixelForValue(Number(point.x)), yScale.getPixelForValue(Number(point.y)));
    });
    ctx.restore();
  },
});
